import { useState } from "react";
import {
  CheckCircle2,
  Phone,
  Mail,
  MessageCircle,
  MapPin,
  ArrowRight,
  Upload,
  ExternalLink,
} from "lucide-react";
import { COMPANY, GOOGLE_FORMS, PANEL_TYPES, IMG } from "@/lib/constants";
import { SectionHeading, EyebrowLabel } from "@/components/SectionHeading";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

const highlights = [
  "Custom-designed panels as per requirement",
  "Compliance with industry standards",
  "Experienced design and engineering support",
  "Quick response and consultation",
  "Competitive pricing",
];

export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    company: "",
    mobile: "",
    email: "",
    panelType: "",
    voltage: "LT",
    location: "",
    quantity: "",
    timeline: "",
    load: "",
    special: "",
    message: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const buildMailto = () => {
    const subject = encodeURIComponent(
      `Panel Requirement — ${form.panelType || "Enquiry"} | ${form.fullName || "Prospect"}`
    );
    const body = encodeURIComponent(
      `Full Name: ${form.fullName}
Company: ${form.company}
Mobile: ${form.mobile}
Email: ${form.email}

Type of Panel: ${form.panelType}
Voltage Level: ${form.voltage}
Project Location: ${form.location}
Quantity Required: ${form.quantity}
Delivery Timeline: ${form.timeline}
Load Details: ${form.load}
Special Requirements: ${form.special}

Message:
${form.message}
`
    );
    return `mailto:${COMPANY.email}?subject=${subject}&body=${body}`;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.fullName || !form.mobile || !form.email) {
      toast.error("Please fill Full Name, Mobile and Email.");
      return;
    }
    setSubmitting(true);

    // If Google Form link is configured, open it in a new tab.
    if (GOOGLE_FORMS.panelRequirement.link) {
      window.open(GOOGLE_FORMS.panelRequirement.link, "_blank", "noopener");
      toast.success("Opening our enquiry form — please complete it there.");
      setSubmitting(false);
      return;
    }

    // Otherwise fall back to a pre-filled email to the company.
    window.location.href = buildMailto();
    toast.success("Opening your email app with the requirement summary.");
    setTimeout(() => setSubmitting(false), 800);
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero */}
      <section className="relative bg-white border-b border-zinc-200 overflow-hidden">
        <div
          className="absolute inset-y-0 right-0 w-1/2 bg-cover bg-center opacity-20 hidden lg:block"
          style={{ backgroundImage: `url('${IMG.controlPanelBlue}')` }}
          aria-hidden
        />
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-transparent to-white hidden lg:block" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-16 md:py-24">
          <EyebrowLabel>Discuss Your Panel Requirement</EyebrowLabel>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter text-navy-950 leading-[1.05] max-w-4xl">
            Tell us your requirement. <br />
            <span className="text-[#EAB308]">We'll engineer the solution.</span>
          </h1>
          <p className="mt-8 max-w-3xl text-lg text-zinc-600 leading-relaxed">
            Have a requirement for electrical panels like MCC, PCC, PMCC, APFC
            or Control Panels? Share your project details — our experts will
            analyze the need and revert with the best technical and commercial
            proposal.
          </p>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {highlights.map((h) => (
              <div
                key={h}
                className="flex items-start gap-2 text-sm font-semibold text-zinc-700"
              >
                <CheckCircle2 className="h-5 w-5 mt-0.5 text-[#EAB308] shrink-0" />
                <span>{h}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form + Contact side */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-28">
          <div className="grid lg:grid-cols-[1.6fr_1fr] gap-12">
            {/* FORM */}
            <div className="border border-zinc-200">
              <div className="p-6 md:p-8 border-b border-zinc-200 bg-zinc-50">
                <h2 className="text-2xl md:text-3xl font-black tracking-tight text-navy-950">
                  Project Enquiry Form
                </h2>
                <p className="mt-2 text-sm text-zinc-600">
                  Please fill the form below to help us understand your
                  requirement better. For training or demo session enquiries,
                  mention in the message section.
                </p>
              </div>

              {/* If the user has provided an embedded Google Form URL, show
                  the Google Form instead of the custom form. */}
              {GOOGLE_FORMS.panelRequirement.embed ? (
                <div className="p-4 md:p-6">
                  <iframe
                    title="Panel Requirement Form"
                    src={GOOGLE_FORMS.panelRequirement.embed}
                    className="w-full"
                    style={{ minHeight: "900px", border: 0 }}
                    data-testid="gform-panel-requirement"
                  >
                    Loading…
                  </iframe>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="requirement-form"
                  className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5"
                >
                  <FieldRow label="Full Name *">
                    <Input
                      value={form.fullName}
                      onChange={(e) => update("fullName", e.target.value)}
                      placeholder="Your name"
                      data-testid="field-fullname"
                      className="rounded-none border-zinc-300 focus-visible:ring-[#EAB308] focus-visible:ring-2 focus-visible:ring-offset-0"
                      required
                    />
                  </FieldRow>
                  <FieldRow label="Company Name">
                    <Input
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      placeholder="Company / Organisation"
                      data-testid="field-company"
                      className="rounded-none border-zinc-300 focus-visible:ring-[#EAB308] focus-visible:ring-2 focus-visible:ring-offset-0"
                    />
                  </FieldRow>
                  <FieldRow label="Mobile Number *">
                    <Input
                      type="tel"
                      inputMode="numeric"
                      value={form.mobile}
                      onChange={(e) => update("mobile", e.target.value)}
                      placeholder="10-digit mobile"
                      data-testid="field-mobile"
                      className="rounded-none border-zinc-300 focus-visible:ring-[#EAB308] focus-visible:ring-2 focus-visible:ring-offset-0"
                      required
                    />
                  </FieldRow>
                  <FieldRow label="Email ID *">
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@example.com"
                      data-testid="field-email"
                      className="rounded-none border-zinc-300 focus-visible:ring-[#EAB308] focus-visible:ring-2 focus-visible:ring-offset-0"
                      required
                    />
                  </FieldRow>

                  <FieldRow label="Type of Panel">
                    <Select
                      value={form.panelType}
                      onValueChange={(v) => update("panelType", v)}
                    >
                      <SelectTrigger
                        data-testid="field-panel-type"
                        className="rounded-none border-zinc-300 focus:ring-[#EAB308] focus:ring-2 focus:ring-offset-0"
                      >
                        <SelectValue placeholder="Select panel type" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {PANEL_TYPES.map((t) => (
                          <SelectItem key={t} value={t} className="rounded-none">
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldRow>
                  <FieldRow label="Voltage Level">
                    <RadioGroup
                      value={form.voltage}
                      onValueChange={(v) => update("voltage", v)}
                      className="flex items-center gap-6 h-10"
                    >
                      {["LT", "MV"].map((v) => (
                        <label
                          key={v}
                          className="flex items-center gap-2 text-sm font-semibold cursor-pointer"
                        >
                          <RadioGroupItem
                            value={v}
                            id={`v-${v}`}
                            data-testid={`field-voltage-${v.toLowerCase()}`}
                            className="border-zinc-400 text-[#EAB308]"
                          />
                          {v}
                        </label>
                      ))}
                    </RadioGroup>
                  </FieldRow>

                  <FieldRow label="Project Location">
                    <Input
                      value={form.location}
                      onChange={(e) => update("location", e.target.value)}
                      placeholder="City / State"
                      data-testid="field-location"
                      className="rounded-none border-zinc-300 focus-visible:ring-[#EAB308] focus-visible:ring-2 focus-visible:ring-offset-0"
                    />
                  </FieldRow>
                  <FieldRow label="Quantity Required">
                    <Input
                      type="number"
                      min="1"
                      value={form.quantity}
                      onChange={(e) => update("quantity", e.target.value)}
                      placeholder="e.g. 2"
                      data-testid="field-quantity"
                      className="rounded-none border-zinc-300 focus-visible:ring-[#EAB308] focus-visible:ring-2 focus-visible:ring-offset-0"
                    />
                  </FieldRow>
                  <FieldRow label="Delivery Timeline">
                    <Input
                      value={form.timeline}
                      onChange={(e) => update("timeline", e.target.value)}
                      placeholder="e.g. Within 6 weeks"
                      data-testid="field-timeline"
                      className="rounded-none border-zinc-300 focus-visible:ring-[#EAB308] focus-visible:ring-2 focus-visible:ring-offset-0"
                    />
                  </FieldRow>
                  <FieldRow label="Load Details">
                    <Input
                      value={form.load}
                      onChange={(e) => update("load", e.target.value)}
                      placeholder="e.g. 250 kW, 415V"
                      data-testid="field-load"
                      className="rounded-none border-zinc-300 focus-visible:ring-[#EAB308] focus-visible:ring-2 focus-visible:ring-offset-0"
                    />
                  </FieldRow>

                  <FieldRow label="Special Requirements" full>
                    <Input
                      value={form.special}
                      onChange={(e) => update("special", e.target.value)}
                      placeholder="PLC, VFD, Automation, IP rating etc."
                      data-testid="field-special"
                      className="rounded-none border-zinc-300 focus-visible:ring-[#EAB308] focus-visible:ring-2 focus-visible:ring-offset-0"
                    />
                  </FieldRow>

                  <FieldRow label="Message" full>
                    <Textarea
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Describe your requirement, scope, site conditions, standards, etc."
                      data-testid="field-message"
                      className="rounded-none border-zinc-300 focus-visible:ring-[#EAB308] focus-visible:ring-2 focus-visible:ring-offset-0 min-h-[130px]"
                    />
                  </FieldRow>

                  <FieldRow label="Upload SLD / Drawings / Specifications" full>
                    <div className="border-2 border-dashed border-zinc-300 p-5 text-sm text-zinc-600 flex items-start gap-3">
                      <Upload className="h-5 w-5 text-[#EAB308] mt-0.5 shrink-0" />
                      <div>
                        For secure file sharing, please email your drawings
                        directly to{" "}
                        <a
                          href={`mailto:${COMPANY.email}`}
                          className="font-bold underline text-navy-900"
                        >
                          {COMPANY.email}
                        </a>{" "}
                        or share via{" "}
                        <a
                          href={`https://wa.me/${COMPANY.whatsapp}`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-bold underline text-navy-900"
                        >
                          WhatsApp
                        </a>
                        . Our team will revert within 24 hours.
                      </div>
                    </div>
                  </FieldRow>

                  <div className="md:col-span-2 pt-3 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between border-t border-zinc-200 mt-3">
                    <p className="text-xs text-zinc-500 uppercase tracking-[0.15em] font-bold">
                      We respond within 24 hours · Mon–Sat
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="submit-requirement"
                      className="inline-flex items-center justify-center gap-2 bg-[#EAB308] hover:bg-[#FACC15] disabled:opacity-60 text-navy-950 px-7 py-4 font-bold uppercase tracking-wide text-sm border-2 border-navy-950"
                    >
                      {submitting ? "Sending..." : "Submit Requirement"}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* CONTACT SIDEBAR */}
            <aside className="space-y-4">
              <div className="border border-zinc-200 p-7 hover:shadow-[6px_6px_0_0_#EAB308] hover:border-navy-950 transition-all">
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308]">
                  Phone
                </div>
                <a
                  href={`tel:${COMPANY.phone}`}
                  data-testid="contact-phone"
                  className="mt-3 flex items-center gap-3 text-xl md:text-2xl font-black tracking-tight text-navy-950 hover:text-[#CA8A04]"
                >
                  <Phone className="h-5 w-5" />
                  {COMPANY.phoneDisplay}
                </a>
              </div>
              <div className="border border-zinc-200 p-7 hover:shadow-[6px_6px_0_0_#EAB308] hover:border-navy-950 transition-all">
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308]">
                  Email
                </div>
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="contact-email"
                  className="mt-3 flex items-center gap-3 text-sm md:text-base font-bold text-navy-950 hover:text-[#CA8A04] break-all"
                >
                  <Mail className="h-5 w-5 shrink-0" />
                  {COMPANY.email}
                </a>
              </div>
              <a
                href={`https://wa.me/${COMPANY.whatsapp}?text=Hi%20Prime%20Volt%2C%20I%27d%20like%20to%20discuss%20a%20panel%20requirement.`}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp"
                className="block bg-[#22C55E] hover:bg-[#16A34A] text-white p-7 transition-colors border-2 border-navy-950"
              >
                <div className="text-xs font-bold uppercase tracking-[0.25em]">
                  WhatsApp
                </div>
                <div className="mt-3 flex items-center gap-3 text-lg font-black tracking-tight">
                  <MessageCircle className="h-5 w-5" />
                  Chat with us now
                  <ExternalLink className="h-4 w-4 ml-auto" />
                </div>
              </a>

              <div className="border border-zinc-200 p-7">
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#EAB308]">
                  Location
                </div>
                <div className="mt-3 flex items-center gap-3 text-sm font-bold text-navy-950">
                  <MapPin className="h-5 w-5" />
                  India — Serving Pan-India Projects
                </div>
              </div>

              <div className="bg-navy-950 text-zinc-300 p-7 border-2 border-navy-950">
                <p className="text-sm leading-relaxed">
                  We provide reliable and cost-effective panel solutions as per
                  industry standards.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}

const FieldRow = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-700">
      {label}
    </Label>
    <div className="mt-2">{children}</div>
  </div>
);
